export const CHATBOT_API_DOC_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>WES Chatbot API – Integration Steps</title>
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { size: A4; margin: 14mm 14mm 16mm; }
  body {
    margin: 0;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 13px;
    line-height: 1.55;
    color: #1a1a1a;
    background: #ffffff;
  }
  main { padding: 0; }
  @media screen { main { max-width: 920px; margin: 0 auto; padding: 32px 24px 48px; } }
  h2 { font-size: 21px; margin: 32px 0 10px; break-after: avoid; page-break-after: avoid; }
  h2:first-child { margin-top: 0; }
  h3 { font-size: 16px; margin: 24px 0 6px; break-after: avoid; page-break-after: avoid; }
  h4 { font-size: 13.5px; margin: 18px 0 6px; break-after: avoid; page-break-after: avoid; }
  p, li { margin: 5px 0; }
  ol, ul { padding-left: 22px; }
  code { font-family: Consolas, "Courier New", monospace; font-size: 12px; background: #f0f0f0; padding: 1px 4px; border-radius: 4px; }
  pre {
    background: #000000;
    color: #ffffff;
    font-family: Consolas, "Courier New", monospace;
    font-size: 11.5px;
    line-height: 1.45;
    padding: 12px 14px;
    border-radius: 6px;
    margin: 8px 0 14px;
    overflow: visible;
    white-space: pre-wrap;
    word-break: break-word;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  pre code { background: none; color: inherit; padding: 0; font-size: inherit; }
  .label { font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; color: #555; margin: 12px 0 0; break-after: avoid; page-break-after: avoid; }
</style>
</head>
<body>
<main>

<h2>1. What WES Team Has Done</h2>
<ol>
  <li>Built three service APIs: recent orders, order details and add to cart.</li>
  <li>Built the token APIs: <code>/api/chat/token</code> (the WES website gets a handoff token) and <code>/api/chat/token/exchange</code> (the chatbot swaps it for a chat token).</li>
  <li>The WES website loads the WesHub widget script on every page.</li>
  <li>When a customer is logged in, the website calls <code>window.WesHub.setToken(handoffToken)</code>.</li>
  <li>About every 19 hours, before the chat token expires, the website calls <code>setToken</code> again with a new handoff token.</li>
  <li>When the customer logs out, the website calls <code>window.WesHub.setToken(null)</code>.</li>
  <li>Allowed <code>https://weshub.wes.com.au</code> in CORS, so the chat frame on <code>https://weshub.wes.com.au</code> can call the APIs.</li>
</ol>

<h3>Get handoff token (done by the WES website)</h3>
<p>The WES website runs this code. It sends the customer's login token to WES, gets a handoff token back, and passes it to the chatbot with <code>setToken</code>. The chatbot team does not run this; it shows where the token comes from and can be used for testing.</p>

<p class="label">Request</p>
<pre><code>POST {BASE_URL}/api/chat/token
Authorization: Bearer &lt;customer login token&gt;</code></pre>

<p class="label">Response</p>
<pre><code>{ "token": "&lt;handoff token&gt;", "expiresIn": 120, "renewIn": 69120 }</code></pre>
<p><code>expiresIn</code> = the handoff token is valid for 120 seconds. <code>renewIn</code> = call this API again after this many seconds (about 19 hours).</p>

<p class="label">Code</p>
<pre><code>let renewTimer = null;

async function getHandoffToken() {
  const loginToken = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
  if (!loginToken) return;

  try {
    const response = await fetch(BASE_URL + "/api/chat/token", {
      method: "POST",
      headers: {
        "Authorization": "Bearer " + loginToken,
        "Content-Type": "application/json",
      },
    });

    const result = await response.json();

    if (response.ok &amp;&amp; result.token) {
      window.WesHub.setToken(result.token);

      clearTimeout(renewTimer);
      renewTimer = setTimeout(getHandoffToken, result.renewIn * 1000);
    } else {
      console.log("Handoff token failed:", response.status, result.code, result.message);
    }
  } catch (error) {
    console.error("Handoff token error:", error);
  } finally {
    console.log("Handoff token request finished");
  }
}

function logoutChat() {
  clearTimeout(renewTimer);
  window.WesHub.setToken(null);
}</code></pre>

<h2>2. What Chatbot Team Will Do</h2>
<p>The chatbot has two parts, and the work is split between them:</p>
<ul>
  <li><strong>widget.js</strong> (<code>https://weshub.wes.com.au/widget.js</code>) runs on the WES page and draws the button and the chat frame. <strong>Step 1</strong> goes here.</li>
  <li><strong>Bot app</strong> (<code>https://weshub.wes.com.au/bot</code>) runs inside the chat frame (iframe) and shows the chat. <strong>Steps 2 to 6</strong> go here.</li>
</ul>

<h3>Step 1: Receive the handoff token (widget.js)</h3>
<p>The WES website calls <code>window.WesHub.setToken(handoffToken)</code>. widget.js must create this function and pass the token into the chat frame with <code>postMessage</code>. If the frame has not loaded yet, keep the token and send it once the frame loads.</p>
<p>The handoff token is valid for <strong>2 minutes</strong> and can be used <strong>once</strong>, so the bot app must exchange it straight away (Step 3).</p>
<pre><code>// inside widget.js, after the chat frame is created
// frame  = the chat iframe
// origin = https://weshub.wes.com.au (already in widget.js)

var frameReady = false;
var pendingToken;

function sendTokenToFrame(handoffToken) {
  frame.contentWindow.postMessage(
    { type: "weshub-token", handoffToken: handoffToken },
    origin
  );
}

frame.addEventListener("load", function () {
  frameReady = true;
  if (pendingToken !== undefined) sendTokenToFrame(pendingToken);
});

window.WesHub = {
  setToken: function (handoffToken) {
    pendingToken = handoffToken;
    if (frameReady) sendTokenToFrame(handoffToken);
  },
};</code></pre>

<h3>Step 2: Receive the token in the bot app</h3>
<p>The bot app listens for the <code>weshub-token</code> message. <code>BASE_URL</code> is taken from <code>event.origin</code>, which is the WES site that sent the message. The same bot app then works on staging (<code>https://staging3.wes.com.au</code>) and production (<code>https://www.wes.com.au</code>) without any change.</p>
<p>Only accept messages from WES sites. A <code>null</code> token means the customer logged out.</p>
<pre><code>const WES_ORIGINS = ["https://www.wes.com.au", "https://staging3.wes.com.au"];

let BASE_URL = null;
let chatToken = null;
let loading = false;

window.addEventListener("message", async function (event) {
  if (!event.data || event.data.type !== "weshub-token") return;
  if (WES_ORIGINS.indexOf(event.origin) === -1) return;

  BASE_URL = event.origin;

  if (!event.data.handoffToken) {
    chatToken = null;
    clearChat();          // your function: clear the chat window
    return;
  }

  await exchangeToken(event.data.handoffToken);
});</code></pre>

<h3>Step 3: Exchange the handoff token for a chat token</h3>
<p class="label">Request</p>
<pre><code>POST {BASE_URL}/api/chat/token/exchange
Content-Type: application/json

{ "handoffToken": "&lt;token from Step 2&gt;" }</code></pre>

<p class="label">Response</p>
<pre><code>{ "token": "&lt;chat token&gt;", "expiresIn": 86400 }</code></pre>
<p>No Authorization header is needed for this call.</p>

<p class="label">Code</p>
<pre><code>async function exchangeToken(handoffToken) {
  loading = true;
  try {
    const response = await fetch(BASE_URL + "/api/chat/token/exchange", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ handoffToken: handoffToken }),
    });

    const result = await response.json();

    if (response.ok &amp;&amp; result.token) {
      chatToken = result.token;
      console.log("Chat token received, valid for", result.expiresIn, "seconds");
    } else {
      chatToken = null;
      console.log("Exchange failed:", response.status, result.code, result.message);
    }
  } catch (error) {
    chatToken = null;
    console.error("Exchange error:", error);
  } finally {
    loading = false;
  }
}</code></pre>

<h3>Step 4: Keep the chat token</h3>
<ul>
  <li>Save the token in memory (a variable). Do not put it in localStorage or cookies.</li>
  <li>It is valid for <strong>24 hours</strong> (<code>expiresIn</code> is in seconds).</li>
  <li>WES sends a new handoff token before it expires, so a new <code>weshub-token</code> message will arrive. Repeat Step 3 each time.</li>
</ul>

<h3>Step 5: Call the service APIs with the chat token</h3>
<p>Send these headers on every call:</p>
<pre><code>Authorization: Bearer &lt;chat token&gt;
Content-Type: application/json</code></pre>

<h4>5a. Recent orders (last 5 orders)</h4>
<p class="label">Code</p>
<pre><code>async function getRecentOrders() {
  if (!chatToken) return null;

  loading = true;
  try {
    const response = await fetch(BASE_URL + "/api/Chat/OrderRecent", {
      method: "POST",
      headers: {
        "Authorization": "Bearer " + chatToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });

    const result = await response.json();

    if (response.ok &amp;&amp; result.data) {
      return result.data;           // result.data.orders = list of orders
    }

    if (response.status === 401) chatToken = null;
    showMessage(result.message);    // your function: show text in the chat
    return null;
  } catch (error) {
    console.error("OrderRecent error:", error);
    showMessage("Could not load your orders. Please try again.");
    return null;
  } finally {
    loading = false;
  }
}</code></pre>
<p class="label">Response</p>
<pre><code>{ "data": { "success": true, "message": "Showing the most recent 5 orders.", "count": 5,
            "orders": [ { "orderId": 422306, "invoiceNo": "SI123456", "orderDate": "28-10-2026", "status": "Shipped" } ] } }</code></pre>

<h4>5b. Order details (send orderId or invoiceNo)</h4>
<p class="label">Code</p>
<pre><code>async function getOrderDetails(orderId, invoiceNo) {
  if (!chatToken) return null;

  const params = orderId ? { orderId: orderId } : { invoiceNo: invoiceNo };

  loading = true;
  try {
    const response = await fetch(BASE_URL + "/api/Chat/OrderDetails", {
      method: "POST",
      headers: {
        "Authorization": "Bearer " + chatToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (response.ok &amp;&amp; result.data) {
      return result.data;           // status, totals, lines, tracking ...
    }

    if (response.status === 401) chatToken = null;
    showMessage(result.message);
    return null;
  } catch (error) {
    console.error("OrderDetails error:", error);
    showMessage("Could not load the order. Please try again.");
    return null;
  } finally {
    loading = false;
  }
}</code></pre>
<p class="label">Response</p>
<pre><code>{ "data": { "success": true, "message": "Order 422306 is Shipped.", "orderId": 422306, "status": "Shipped",
            "payment": {...}, "shippingAddress": {...}, "billingAddress": {...}, "tracking": {...},
            "totals": {...}, "lines": [ { "sku": "ABC10", "description": "...", "qty": 2 } ] } }</code></pre>

<h4>5c. Add to cart (qty is added to what is already in the cart, max 999)</h4>
<p class="label">Code</p>
<pre><code>async function addToCart(sku, qty) {
  if (!chatToken) return null;

  loading = true;
  try {
    const response = await fetch(BASE_URL + "/api/Chat/AddToCart", {
      method: "POST",
      headers: {
        "Authorization": "Bearer " + chatToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ sku: sku, qty: qty }),
    });

    const result = await response.json();

    if (response.ok &amp;&amp; result.data) {
      showMessage(result.data.message);
      return result.data;           // cart lines and summary
    }

    if (response.status === 401) chatToken = null;
    showMessage(result.message);
    return null;
  } catch (error) {
    console.error("AddToCart error:", error);
    showMessage("Could not add to cart. Please try again.");
    return null;
  } finally {
    loading = false;
  }
}</code></pre>
<p class="label">Response</p>
<pre><code>{ "data": { "success": true, "message": "Added 2 x ABC10 to the cart. This line now has 2.",
            "cart": [ ... ], "summary": { "orderId": 431002, "lines": 1, "totalIncTax": 0 } } }</code></pre>

<p>If a call fails, the response is <code>{ "message": "...", "code": "..." }</code>. The <code>message</code> can be shown to the customer.</p>

<h3>Step 6: When the token stops working</h3>
<ul>
  <li><strong>A null token arrives:</strong> the customer logged out. Delete the chat token and clear the chat.</li>
  <li><strong>A service API returns 401:</strong> the chat token expired. Delete it and wait for the next <code>weshub-token</code> message, then repeat Step 3.</li>
  <li><strong>The exchange returns 401 (invalid_handoff):</strong> the handoff token was older than 2 minutes or already used. Wait for the next <code>weshub-token</code> message.</li>
</ul>

</main>
</body>
</html>
`;
